import { create } from 'zustand';

// Installing the web app (PWA): iPhone/iPad via Share → Add to Home Screen; Chrome/Edge on
// desktop and Android offer a one-tap install we can trigger ourselves.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
export const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
export const isAndroid = /Android/i.test(ua);
export const isStandalone = () =>
  typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

export const useInstall = create<{ prompt: BeforeInstallPromptEvent | null; installed: boolean }>(() => ({ prompt: null, installed: false }));

export function installPWA() {
  const p = useInstall.getState().prompt;
  if (!p) return Promise.resolve(false);
  return p.prompt().then(() => p.userChoice.then((c) => c.outcome === 'accepted'));
}

export function registerPWA(inAndroidApp: boolean) {
  if (typeof window === 'undefined' || inAndroidApp || !('serviceWorker' in navigator) || import.meta.env.DEV) return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    useInstall.setState({ prompt: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => useInstall.setState({ prompt: null, installed: true }));
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}

// ---- updates ---------------------------------------------------------------------------
// A new release is noticed by comparing /version.json with the build this page runs. When it's
// safe (no room joined, nothing being typed, or the app is in the background) the app reloads
// itself; otherwise it shows "Update" and applies it after you leave the room.

export interface ReleaseNotes {
  id: string;
  title: string;
  items: string[];
}

/** ready: a newer release is waiting; notes: what it adds (from its version.json). */
export const useUpdate = create<{ ready: boolean; notes: ReleaseNotes | null }>(() => ({ ready: false, notes: null }));

let applying = false;
export function applyUpdate() {
  if (applying) return;
  applying = true;
  navigator.serviceWorker?.getRegistration().then((r) => r?.waiting?.postMessage('skipWaiting')).catch(() => undefined);
  location.reload();
}

function safeToReload(inRoom: () => boolean) {
  if (inRoom()) return false;
  if (document.visibilityState === 'hidden') return true;
  const el = document.activeElement as HTMLElement | null;
  return !(el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)));
}

export function startUpdateChecks(inRoom: () => boolean) {
  if (import.meta.env.DEV || typeof window === 'undefined') return;
  const check = async () => {
    try {
      navigator.serviceWorker?.getRegistration().then((r) => r?.update()).catch(() => undefined);
      const r = await fetch('/version.json', { cache: 'no-store' });
      if (!r.ok) return;
      const { build, notes } = (await r.json()) as { build?: string; notes?: ReleaseNotes };
      if (!build || build === __BUILD_ID__) return;
      if (safeToReload(inRoom)) applyUpdate();
      else useUpdate.setState({ ready: true, notes: notes ?? null });
    } catch {
      /* offline: try again later */
    }
  };
  setTimeout(check, 10_000);
  setInterval(check, 5 * 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
    else if (useUpdate.getState().ready && !inRoom()) applyUpdate(); // switched away: update unnoticed
  });
  window.addEventListener('online', check);
  // A lazily loaded part of an older release may be gone after a deploy: load the new release.
  // Builds keep earlier builds' files for 48 h (vite.config.ts), so this is rare. In a room, never
  // reload by surprise (it drops you out of the room): that part shows "Update" instead.
  window.addEventListener('vite:preloadError', (e) => {
    if (inRoom()) {
      useUpdate.setState({ ready: true });
      return; // the import fails and PartBoundary (AppShell) offers the update
    }
    e.preventDefault();
    applyUpdate();
  });
}
